import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { ReactComponent as IconTag } from "@shared/assets/Icons/IconTag.svg";
import { ReactComponent as IconCheck } from "@shared/assets/Icons/IconCheck.svg";

import {
  type ResourceTagPickerCandidate,
  type ResourceTagSummary,
  type UseResourceTagsReturn,
} from "./useResourceTags";

/**
 * Resource-side tag picker (buffered Save/Cancel flow against the global Tags
 * Manager).
 *
 * The modal is **attach/detach only** — tag creation lives in
 * `/account/tags-manager` (full wizard with color, category, description).
 * The user composes the desired tag set in memory by clicking candidates in
 * the **Available** panel and removing chips in the **Selected** panel; no
 * call hits the server until **Save** is pressed. Save persists the entire
 * intended set with a single transactional `PUT §5.2` (the backend applies
 * the diff atomically). **Cancel** closes the modal and discards every
 * pending change without touching the resource.
 *
 * The `useResourceTags` controller is owned by the parent
 * (`EditableTagList`) and passed in via the `controller` prop. That keeps
 * the chip strip and the modal pointing at one shared `assignedTags` so a
 * Save in the modal updates the strip immediately without a detail-GET
 * refetch — and avoids a second hook instance racing with the first.
 *
 * @see `EditableTagList.tsx` — owns the hook and decides legacy vs picker.
 */

interface TagManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shared `useResourceTags` instance (assignedTags + mutations). */
  controller: UseResourceTagsReturn;
  /** Optional cap on how many tags can be attached at once (UI-only — §13.6). */
  maxTags?: number;
  /** Notified after a successful Save with the persisted tag list. */
  onSaved?: (tags: ResourceTagSummary[]) => void;
}

const FALLBACK_DOT = "var(--color-primitive-gray-300)";
const SEARCH_DEBOUNCE_MS = 250;
const PICKER_PAGE_LIMIT = 20;
const TAGS_MANAGER_ROUTE = "/account/tags-manager";

const TagSwatch: React.FC<{ color: string | null }> = ({ color }) => (
  <span
    aria-hidden
    className="w-3 h-3 rounded-sm flex-shrink-0 border border-semantic-border-secondary"
    style={{ background: color ?? FALLBACK_DOT }}
  />
);

/**
 * Minimal display shape used for both panels — covers the assigned-tag
 * superset (`ResourceTagSummary`) and the picker candidates which lack
 * `category` (§3.9 omits it intentionally).
 */
interface DisplayTag {
  id: string;
  name: string;
  color: string | null;
  category: ResourceTagSummary["category"];
}

const candidateToDisplay = (c: ResourceTagPickerCandidate): DisplayTag => ({
  id: c.id,
  name: c.name,
  color: c.color,
  category: null,
});

const summaryToDisplay = (t: ResourceTagSummary): DisplayTag => ({
  id: t.id,
  name: t.name,
  color: t.color,
  category: t.category,
});

export const TagManagementModal: React.FC<TagManagementModalProps> = ({
  isOpen,
  onClose,
  controller,
  maxTags,
  onSaved,
}) => {
  const {
    assignedTags,
    loading: assignedLoading,
    saving,
    error: hookError,
    clearError: clearHookError,
    searchPickerCandidates,
    replace,
  } = controller;

  // Buffered selection — the user composes the intended id set in memory
  // and only persists on Save. Cancel discards everything.
  const [desiredIds, setDesiredIds] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [candidates, setCandidates] = useState<ResourceTagPickerCandidate[]>(
    []
  );
  const [pickerLoading, setPickerLoading] = useState<boolean>(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  // Tracks the latest picker request so a stale one can't overwrite a
  // newer response (typing faster than the network can answer).
  const pickerRequestIdRef = useRef(0);
  // Guards the one-shot seeding of `desiredIds` from `assignedTags` per
  // open session. Without it, every subsequent change to `assignedTags`
  // (e.g. after a Save commits) would re-overwrite pending edits — and a
  // dep on `assignedTags` would re-fire the open-reset effect in a loop.
  const desiredSeededRef = useRef(false);

  // Open / close lifecycle. Only depends on `isOpen` so opening a modal
  // never enters a reset loop driven by `assignedTags` updates.
  useEffect(() => {
    if (!isOpen) {
      desiredSeededRef.current = false;
      return;
    }
    setSearch("");
    setDebouncedSearch("");
    setApiError(null);
    setPickerError(null);
  }, [isOpen]);

  // Seed `desiredIds` from `assignedTags` exactly once per open session,
  // as soon as the hook hands us a list (either the inline-§5.5 seed or
  // a freshly resolved §5.1 fetch). After that the ref guard keeps later
  // `assignedTags` changes from clobbering the user's pending selection.
  useEffect(() => {
    if (!isOpen) return;
    if (desiredSeededRef.current) return;
    if (assignedLoading) return;
    desiredSeededRef.current = true;
    setDesiredIds(new Set(assignedTags.map((t) => t.id)));
  }, [isOpen, assignedTags, assignedLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, isOpen]);

  // Re-query candidates when the search changes. We deliberately do NOT
  // refetch on every desiredIds change: the server-side `excludeFor*` only
  // hides tags currently in the pivot, but pending detaches are still in
  // the pivot until Save. Local filtering covers that case.
  useEffect(() => {
    if (!isOpen) return;
    const requestId = ++pickerRequestIdRef.current;
    setPickerLoading(true);
    setPickerError(null);
    void searchPickerCandidates({
      search: debouncedSearch || undefined,
      limit: PICKER_PAGE_LIMIT,
    })
      .then((items) => {
        if (pickerRequestIdRef.current !== requestId) return;
        setCandidates(items);
      })
      .catch((err) => {
        if (pickerRequestIdRef.current !== requestId) return;
        setCandidates([]);
        setPickerError(
          err instanceof Error
            ? err.message
            : "Failed to load tag suggestions"
        );
      })
      .finally(() => {
        if (pickerRequestIdRef.current === requestId) {
          setPickerLoading(false);
        }
      });
  }, [isOpen, debouncedSearch, searchPickerCandidates]);

  // Single source of truth for tag display info: the union of currently
  // assigned tags and picker candidates. Selected and Available panels
  // project from this map by membership in `desiredIds`.
  const tagById = useMemo(() => {
    const m = new Map<string, DisplayTag>();
    for (const t of assignedTags) m.set(t.id, summaryToDisplay(t));
    for (const c of candidates) {
      if (!m.has(c.id)) m.set(c.id, candidateToDisplay(c));
    }
    return m;
  }, [assignedTags, candidates]);

  const selectedDisplay: DisplayTag[] = useMemo(() => {
    const out: DisplayTag[] = [];
    for (const id of Array.from(desiredIds)) {
      const t = tagById.get(id);
      if (t) out.push(t);
    }
    return out;
  }, [desiredIds, tagById]);

  // Available = anything we know about that isn't desired AND matches the
  // current search. Includes pending-detached assigned tags so the user
  // can re-select them without losing their place.
  const availableDisplay: DisplayTag[] = useMemo(() => {
    const seen = new Set<string>();
    const out: DisplayTag[] = [];
    const pushIfMatch = (t: DisplayTag | undefined) => {
      if (!t) return;
      if (desiredIds.has(t.id)) return;
      if (seen.has(t.id)) return;
      const q = debouncedSearch.toLowerCase();
      if (q && !t.name.toLowerCase().includes(q)) return;
      seen.add(t.id);
      out.push(t);
    };
    for (const c of candidates) pushIfMatch(candidateToDisplay(c));
    for (const t of assignedTags) pushIfMatch(summaryToDisplay(t));
    return out;
  }, [candidates, assignedTags, desiredIds, debouncedSearch]);

  const trimmedSearch = search.trim();

  const isAtMax =
    maxTags !== undefined && desiredIds.size >= maxTags;

  const initialIds = useMemo(
    () => new Set(assignedTags.map((t) => t.id)),
    [assignedTags]
  );

  const { hasChanges, addCount, removeCount } = useMemo(() => {
    let adds = 0;
    let removes = 0;
    for (const id of Array.from(desiredIds)) {
      if (!initialIds.has(id)) adds += 1;
    }
    for (const id of Array.from(initialIds)) {
      if (!desiredIds.has(id)) removes += 1;
    }
    return {
      hasChanges: adds > 0 || removes > 0,
      addCount: adds,
      removeCount: removes,
    };
  }, [desiredIds, initialIds]);

  const handleAttachToggle = useCallback(
    (tag: DisplayTag) => {
      if (saving) return;
      if (isAtMax) {
        setApiError(
          `Maximum ${maxTags} tags allowed. Remove one to add another.`
        );
        return;
      }
      setApiError(null);
      setDesiredIds((prev) => {
        if (prev.has(tag.id)) return prev;
        const next = new Set(prev);
        next.add(tag.id);
        return next;
      });
    },
    [isAtMax, maxTags, saving]
  );

  const handleDetachToggle = useCallback(
    (tagId: string) => {
      if (saving) return;
      setApiError(null);
      setDesiredIds((prev) => {
        if (!prev.has(tagId)) return prev;
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });
    },
    [saving]
  );

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!hasChanges) {
      onClose();
      return;
    }
    setApiError(null);
    try {
      const next = await replace(Array.from(desiredIds));
      onSaved?.(next);
      onClose();
    } catch (err) {
      setApiError(
        err instanceof Error ? err.message : "Failed to save tag changes"
      );
    }
  }, [desiredIds, hasChanges, onClose, onSaved, replace, saving]);

  // Reset every error source the alert may be bound to. Without this,
  // closing the alert only resets `apiError` and the hook/picker errors
  // keep the alert anchored — and the alert blocks the modal — so the
  // user has to refresh the page to escape. Called by the alert's
  // OK / × handlers, and also by Cancel / backdrop close so a stale
  // error doesn't survive into the next open of the modal.
  const dismissError = useCallback(() => {
    setApiError(null);
    setPickerError(null);
    clearHookError();
  }, [clearHookError]);

  const handleCancel = useCallback(() => {
    if (saving) return;
    dismissError();
    onClose();
  }, [dismissError, onClose, saving]);

  const modalTitle = (
    <div className="flex items-center gap-2">
      <div className="flex p-1 border border-semantic-border-secondary bg-semantic-bg-secondary rounded items-center justify-center">
        <IconTag className="w-5 h-5 text-semantic-text-primary" />
      </div>
      <span className="font-semibold text-semantic-text-primary">
        Edit tags
      </span>
    </div>
  );

  const visibleError = apiError ?? hookError ?? pickerError;

  const saveLabel = saving
    ? "Saving..."
    : hasChanges
    ? `Save (${addCount + removeCount})`
    : "Save";

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={modalTitle}
      size="lg"
      closeOnBackdropClick={!saving}
    >
      <div className="px-2 sm:px-5">
        <div className="flex flex-col gap-4 h-full">
          {visibleError && (
            <GenericAlert
              isOpen={true}
              onClose={dismissError}
              onConfirm={dismissError}
              title="Error"
              description={visibleError}
              variant="error"
              confirmLabel="OK"
            />
          )}

          <div className="flex flex-col gap-2">
            <p className="text-sm text-semantic-text-secondary">
              Click a tag in <span className="font-medium">Available</span> to
              add it to the selection, or click the ×{" "}
              on a selected chip to remove it. Click{" "}
              <span className="font-medium">Save</span> to apply your changes.
            </p>
            <div className="text-xs px-3 py-2 rounded-md bg-semantic-bg-secondary border border-semantic-border-secondary text-semantic-text-secondary">
              This modal only attaches or detaches existing tags. To create a
              new tag, go to{" "}
              <Link
                to={TAGS_MANAGER_ROUTE}
                className="text-semantic-text-brand underline hover:text-semantic-text-primary"
              >
                Account → Tags Manager
              </Link>
              .
            </div>
            <GenericInput
              value={search}
              size="small"
              label="Find tags"
              placeholder="Search tags by name…"
              onChange={(val) => {
                setSearch(val);
                setApiError(null);
              }}
              disabled={saving}
              fullWidth
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-semantic-text-secondary">
                Selected ({selectedDisplay.length}
                {maxTags !== undefined ? ` / ${maxTags}` : ""})
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 min-h-[28px] p-2 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary">
              {assignedLoading && selectedDisplay.length === 0 ? (
                <span className="text-xs text-semantic-text-secondary italic">
                  Loading…
                </span>
              ) : selectedDisplay.length === 0 ? (
                <span className="text-xs text-semantic-text-secondary italic">
                  No tags selected.
                </span>
              ) : (
                selectedDisplay.map((t) => (
                  <span
                    key={t.id}
                    className="relative inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-semantic-text-primary border border-semantic-border-secondary overflow-hidden"
                    style={t.color ? { borderColor: t.color } : undefined}
                    title={t.category?.name ?? undefined}
                  >
                    <span
                      aria-hidden
                      className="absolute inset-0 opacity-40"
                      style={{ background: t.color ?? FALLBACK_DOT }}
                    />
                    <span className="relative">{t.name}</span>
                    <button
                      type="button"
                      onClick={() => handleDetachToggle(t.id)}
                      disabled={saving}
                      aria-label={`Remove ${t.name}`}
                      className="relative text-semantic-text-primary hover:text-semantic-text-error disabled:opacity-50"
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-semantic-text-secondary">
              Available
            </span>
            <div className="rounded-md border border-semantic-border-secondary divide-y divide-semantic-border-secondary max-h-[280px] overflow-y-auto">
              {pickerLoading && availableDisplay.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-semantic-text-secondary">
                  Loading suggestions…
                </div>
              ) : availableDisplay.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-semantic-text-secondary">
                  {trimmedSearch
                    ? `No tags match "${trimmedSearch}".`
                    : "No more tags to attach. Create new tags in Tags Manager."}
                </div>
              ) : (
                availableDisplay.map((tag) => {
                  const disabled = saving || isAtMax;
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleAttachToggle(tag)}
                      disabled={disabled}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-semantic-state-active disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <TagSwatch color={tag.color} />
                      <span className="flex-1 min-w-0 truncate text-semantic-text-primary">
                        {tag.name}
                      </span>
                      {tag.category && (
                        <span className="text-xs text-semantic-text-secondary truncate max-w-[140px]">
                          {tag.category.name}
                        </span>
                      )}
                      <IconCheck className="w-4 h-4 text-semantic-text-secondary flex-shrink-0 opacity-0 group-hover:opacity-100" />
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-semantic-border-secondary flex-shrink-0 mt-2">
            <GenericButton
              label="Cancel"
              variant="secondary"
              onClick={handleCancel}
              disabled={saving}
            />
            <GenericButton
              label={saveLabel}
              variant="brand"
              onClick={() => void handleSave()}
              disabled={saving || !hasChanges}
              loading={saving}
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};
